package redmcrosco.alex.farmatch.api

import io.ktor.client.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.client.call.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import redmcrosco.alex.farmatch.*
import redmcrosco.alex.farmatch.models.*

class FarmatchApi {
    private val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                useAlternativeNames = false
            })
        }
        install(Logging) {
            level = LogLevel.ALL
        }
        install(io.ktor.client.plugins.HttpTimeout) {
            requestTimeoutMillis = 60_000 // Aspetta fino a 60 secondi
            connectTimeoutMillis = 60_000
            socketTimeoutMillis = 60_000
        }
    }

    suspend fun login(request: LoginRequest): LoginResponse {
        return try {
            val response = client.post("$BASE_URL/login") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            response.body<LoginResponse>()
        } catch (e: Exception) {
            LoginResponse(success = false, message = e.message ?: "Errore di connessione")
        }
    }

    suspend fun requestPasswordReset(email: String): PasswordResetResponse {
        println("DEBUG API: requestPasswordReset - base: $BASE_URL, email: $email")
        return try {
            val response = client.post("$BASE_URL/auth/password-reset/request") {
                contentType(ContentType.Application.Json)
                setBody(PasswordResetRequest(email))
            }
            println("DEBUG API: requestPasswordReset status: ${response.status}")
            val body = response.body<PasswordResetResponse>()
            println("DEBUG API: requestPasswordReset body: $body")
            body
        } catch (e: Exception) {
            println("DEBUG API: requestPasswordReset ERROR: ${e.message}")
            PasswordResetResponse(success = false, message = e.message ?: "Errore di connessione")
        }
    }

    suspend fun confirmPasswordReset(email: String, code: String, newPassword: String): PasswordResetResponse {
        println("DEBUG API: confirmPasswordReset - base: $BASE_URL, email: $email, code: $code")
        return try {
            val response = client.post("$BASE_URL/auth/password-reset/confirm") {
                contentType(ContentType.Application.Json)
                setBody(PasswordResetConfirmRequest(email, code, newPassword))
            }
            println("DEBUG API: confirmPasswordReset status: ${response.status}")
            val body = response.body<PasswordResetResponse>()
            println("DEBUG API: confirmPasswordReset body: $body")
            body
        } catch (e: Exception) {
            println("DEBUG API: confirmPasswordReset ERROR: ${e.message}")
            PasswordResetResponse(success = false, message = e.message ?: "Errore di connessione")
        }
    }

    suspend fun registerFarmacista(farmacista: Farmacista): Boolean {
        return try {
            val response = client.post("$BASE_URL/register/farmacista") {
                contentType(ContentType.Application.Json)
                setBody(farmacista)
            }
            response.status == HttpStatusCode.Created
        } catch (e: Exception) {
            false
        }
    }

    suspend fun registerFarmacia(farmacia: Farmacia): Boolean {
        return try {
            val response = client.post("$BASE_URL/register/farmacia") {
                contentType(ContentType.Application.Json)
                setBody(farmacia)
            }
            response.status == HttpStatusCode.Created
        } catch (e: Exception) {
            false
        }
    }

    suspend fun updateFarmacistaProfilo(email: String, farmacista: Farmacista): Boolean {
        return try {
            val response = client.patch("$BASE_URL/api/farmacista/profilo") {
                parameter("email", email)
                contentType(ContentType.Application.Json)
                setBody(farmacista)
            }
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun updateFarmaciaProfilo(email: String, farmacia: Farmacia): Boolean {
        return try {
            val response = client.patch("$BASE_URL/api/farmacia/profilo") {
                parameter("email", email)
                contentType(ContentType.Application.Json)
                setBody(farmacia)
            }
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun createAnnuncio(annuncio: AnnuncioTurno): String {
        return try {
            val response = client.post("$BASE_URL/api/annunci") {
                contentType(ContentType.Application.Json)
                setBody(annuncio)
            }
            if (response.status == HttpStatusCode.Created) "SUCCESS"
            else if (response.status == HttpStatusCode.Conflict) "CONFLICT"
            else "ERROR"
        } catch (e: Exception) {
            "ERROR"
        }
    }

    suspend fun createBatchAnnunci(request: BatchAnnuncioRequest): String {
        println("DEBUG API: createBatchAnnunci - invio ${request.entries.size} turni")
        return try {
            val response = client.post("$BASE_URL/api/annunci") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            println("DEBUG API: createBatchAnnunci status: ${response.status}")
            if (response.status == HttpStatusCode.Created) "SUCCESS"
            else if (response.status == HttpStatusCode.Conflict) "CONFLICT"
            else "ERROR"
        } catch (e: Exception) {
            println("DEBUG API: createBatchAnnunci EXCEPTION: ${e.message}")
            "ERROR"
        }
    }

    suspend fun searchFarmacie(
        query: String = "",
        regione: String = "",
        provincia: String = "",
        comune: String = "",
        cap: String = "",
        startDate: Long? = null,
        endDate: Long? = null,
        myEmail: String? = null
    ): List<Farmacia> {
        println("DEBUG API: searchFarmacie - base: $BASE_URL, query: $query, regione: $regione")
        return try {
            val response = client.get("$BASE_URL/farmacie/search") {
                if (query.isNotEmpty()) parameter("query", query)
                if (regione.isNotEmpty()) parameter("regione", regione)
                if (provincia.isNotEmpty()) parameter("provincia", provincia)
                if (comune.isNotEmpty()) parameter("comune", comune)
                if (cap.isNotEmpty()) parameter("cap", cap)
                if (startDate != null) parameter("startDate", startDate)
                if (endDate != null) parameter("endDate", endDate)
                if (myEmail != null) parameter("myEmail", myEmail)
            }
            println("DEBUG API: searchFarmacie status: ${response.status}")
            response.body()
        } catch (e: Exception) {
            println("DEBUG API: searchFarmacie EXCEPTION: ${e.message}")
            emptyList()
        }
    }

    suspend fun searchFarmacisti(
        query: String = "",
        comune: String = "",
        cap: String = "",
        startDate: Long? = null,
        endDate: Long? = null,
        myEmail: String? = null,
        competenza: String = ""
    ): List<Farmacista> {
        return try {
            val response = client.get("$BASE_URL/farmacisti/search") {
                if (query.isNotEmpty()) parameter("query", query)
                if (comune.isNotEmpty()) parameter("comune", comune)
                if (cap.isNotEmpty()) parameter("cap", cap)
                if (startDate != null) parameter("startDate", startDate)
                if (endDate != null) parameter("endDate", endDate)
                if (myEmail != null) parameter("myEmail", myEmail)
                if (competenza.isNotEmpty()) parameter("competenza", competenza)
            }
            response.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getAnnunci(
        role: String? = null,
        startDate: Long? = null,
        endDate: Long? = null,
        provincia: String? = null,
        comune: String? = null,
        email: String? = null
    ): List<AnnuncioTurno> {
        return try {
            val response = client.get("$BASE_URL/api/annunci") {
                if (role != null) parameter("role", role)
                if (startDate != null) parameter("startDate", startDate)
                if (endDate != null) parameter("endDate", endDate)
                if (provincia != null) parameter("provincia", provincia)
                if (comune != null) parameter("comune", comune)
                if (email != null) parameter("email", email)
            }
            response.body()
        } catch (e: Exception) {
            emptyList()
        }
    }
    
    // Rimosso overload ambiguo per email


    suspend fun getAnnuncioById(id: String): AnnuncioTurno? {
        return try {
            val response = client.get("$BASE_URL/api/annunci/$id")
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    suspend fun getFarmacistaByEmail(email: String): Farmacista? {
        return try {
            val response = client.get("$BASE_URL/api/farmacista/profilo") {
                parameter("email", email)
            }
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    suspend fun getFarmaciaByEmail(email: String): Farmacia? {
        return try {
            val response = client.get("$BASE_URL/api/farmacia/profilo") {
                parameter("email", email)
            }
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    suspend fun getFarmacistaById(id: String): Farmacista? {
        return try {
            val response = client.get("$BASE_URL/api/farmacista/$id")
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    suspend fun getFarmaciaById(id: String): Farmacia? {
        return try {
            val response = client.get("$BASE_URL/api/farmacia/$id")
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    suspend fun updateAnnuncio(id: String, annuncio: AnnuncioTurno): Boolean {
        return try {
            val response = client.patch("$BASE_URL/api/annunci/$id") {
                contentType(ContentType.Application.Json)
                setBody(annuncio)
            }
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun deleteAnnuncio(id: String): Boolean {
        return try {
            val response = client.delete("$BASE_URL/api/annunci/$id")
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun deleteBatchAnnunci(ids: List<String>): Boolean {
        return try {
            val response = client.post("$BASE_URL/api/annunci/batch-delete") {
                contentType(ContentType.Application.Json)
                setBody(ids)
            }
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun getMatches(email: String, role: String): List<Match> {
        return try {
            val response = client.get("$BASE_URL/api/matches") {
                parameter("email", email)
                parameter("role", role)
            }
            response.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun respondToMatch(matchId: String, action: String, email: String, role: String): Pair<Boolean, String?> {
        return try {
            val response = client.post("$BASE_URL/api/matches/$matchId/respond") {
                parameter("action", action)
                parameter("email", email)
                parameter("role", role)
            }
            if (response.status == HttpStatusCode.OK) {
                Pair<Boolean, String?>(true, null)
            } else {
                val errorMsg = try { response.body<String>() } catch (e: Exception) { "" }
                Pair<Boolean, String?>(false, errorMsg.ifBlank { "Errore durante l'operazione" })
            }
        } catch (e: Exception) {
            Pair<Boolean, String?>(false, e.message ?: "Errore di connessione")
        }
    }

    // --- PREFERITI ---
    suspend fun getPreferiti(utenteId: String): List<Preferito> {
        return try {
            client.get("$BASE_URL/api/preferiti") {
                parameter("utenteId", utenteId)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun togglePreferito(preferito: Preferito): Boolean {
        return try {
            val response = client.post("$BASE_URL/api/preferiti/toggle") {
                contentType(ContentType.Application.Json)
                setBody(preferito)
            }
            response.status == HttpStatusCode.OK || response.status == HttpStatusCode.Created
        } catch (e: Exception) {
            false
        }
    }

    suspend fun sendDirectProposal(targetId: String, targetRuolo: String, message: String, myEmail: String): Boolean {
        return try {
            val response = client.post("$BASE_URL/api/matches/direct-proposal") {
                parameter("targetId", targetId)
                parameter("targetRuolo", targetRuolo)
                parameter("myEmail", myEmail)
                contentType(ContentType.Application.Json)
                setBody(message)
            }
            response.status == HttpStatusCode.OK || response.status == HttpStatusCode.Created
        } catch (e: Exception) {
            false
        }
    }

    suspend fun getNotifications(utenteId: String): List<Notification> {
        return try {
            client.get("$BASE_URL/api/notifications") {
                parameter("utenteId", utenteId)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getStorico(utenteId: String, role: String): List<StoricoEntry> {
        return try {
            client.get("$BASE_URL/api/matches/storico") {
                parameter("utenteId", utenteId)
                parameter("role", role)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun creaFatturaProforma(annuncioIds: List<String>, farmacistaId: String, farmaciaId: String): FatturaEntry? {
        return try {
            val idsParam = annuncioIds.joinToString(",")
            client.post("$BASE_URL/api/matches/fatture/crea") {
                parameter("annuncioIds", idsParam)
                parameter("farmacistaId", farmacistaId)
                parameter("farmaciaId", farmaciaId)
            }.body()
        } catch (e: Exception) {
            null
        }
    }

    suspend fun getStoricoFatture(utenteId: String, role: String): List<FatturaEntry> {
        return try {
            client.get("$BASE_URL/api/matches/fatture/storico") {
                parameter("utenteId", utenteId)
                parameter("role", role)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun markNotificationsAsRead(utenteId: String): Boolean {
        return try {
            val response = client.post("$BASE_URL/api/notifications/mark-read") {
                parameter("utenteId", utenteId)
            }
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun deleteNotification(id: String): Boolean {
        return try {
            val response = client.delete("$BASE_URL/api/notifications/$id")
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    // --- NUOVE API GEOGRAFICHE ---
    suspend fun getRegioni(): List<String> {
        return try {
            client.get("$BASE_URL/api/geo/regioni").body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getProvince(regione: String): List<String> {
        return try {
            client.get("$BASE_URL/api/geo/province") {
                parameter("regione", regione)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getComuni(provincia: String): List<String> {
        return try {
            client.get("$BASE_URL/api/geo/comuni") {
                parameter("provincia", provincia)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getCap(comune: String): List<String> {
        return try {
            client.get("$BASE_URL/api/geo/cap") {
                parameter("comune", comune)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun searchComuni(query: String): List<String> {
        return try {
            client.get("$BASE_URL/api/geo/comuni/search") {
                parameter("query", query)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getSubscriptionStatus(userId: String): UserStatus {
        // Chiamiamo il BACKEND (PAYMENT_API_URL) e non la vetrina
        return client.get("$PAYMENT_API_URL/v1/status/FAR_MATCH/$userId").body()
    }
}
